import { Service } from '@angular/core';

const DB_NAME = 'VDR';
const DB_VERSION = 1;
const STORE_NAME = 'storage';

@Service()
export class IndexedDbService {
  available(): boolean {
    return window.indexedDB !== undefined;
  }

  async clear(): Promise<void> {
    return this.#action((objectStore) => objectStore.clear());
  }

  async getItem<T>(key: string): Promise<T | null> {
    return this.#action((objectStore) => objectStore.get(key), 'readonly');
  }

  async removeItem(key: string): Promise<void> {
    return this.#action((objectStore) => objectStore.delete(key));
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    return this.#action((objectStore) => objectStore.put(value, key));
  }

  async #action<T = void>(fn: (store: IDBObjectStore) => IDBRequest, mode: IDBTransactionMode = 'readwrite'): Promise<T> {
    const db = await this.#openDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = fn(objectStore);

      transaction.onerror = () => reject(transaction.error);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve((request.result ?? null) as T);
    });
  }

  #openDatabase(): Promise<IDBDatabase> {
    if (!this.available()) {
      throw new Error('IndexedDB is not supported');
    }

    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (db.objectStoreNames.contains(STORE_NAME)) {
          return;
        }

        const store = db.createObjectStore(STORE_NAME);

        store.createIndex('key', 'key', { unique: true });
        store.createIndex('value', 'value', { unique: false });
      };
    });
  }
}
